import { Bell } from 'lucide-react';
import * as React from 'react';

import { useNotificationSummary } from '@/api/use-notifications';
import { NotificationPanel } from '@/components/notification-panel';
import { useNotificationActivate } from '@/components/use-notification-activate';

// =============================================================================
// notification-bell.tsx — Cloche de navbar + badge non-lus (R15-COM-06).
// =============================================================================
// Icône navbar (lucide Bell) + badge NUMÉRIQUE des non-lus (CACHÉ si 0). Ouvre le volet consolidé
// (NotificationPanel / shadcn Sheet). Clic sur un item : PATCH read (badge recalculé serveur) puis
// NAVIGATION résolue depuis `payloadContext` (mapping catégorie→route ; chat/annonces = pas de route V1,
// mark-read seul). Le compteur vient du MÊME cache `summary` que le widget D-04 (DEC-31).
//
// A11y (ST-NFR-03) : le bouton porte un aria-label DYNAMIQUE annonçant le nombre de non-lus ; le badge
// visuel est un renfort (aria-hidden). Navigation clavier native (button). Tokens sémantiques uniquement.

/** Formate le compteur affiché dans le badge (borne 99+ pour rester compact). */
function badgeText(count: number): string {
  return count > 99 ? '99+' : String(count);
}

export function NotificationBell(): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  const summary = useNotificationSummary();
  const closePanel = React.useCallback((): void => {
    setOpen(false);
  }, []);
  // Activation PARTAGÉE (mark-read + navigation) ; le volet se ferme après activation d'un item.
  const onItemActivate = useNotificationActivate(closePanel);

  const unreadCount = summary.data?.unreadCount ?? 0;
  const hasUnread = unreadCount > 0;

  const label = hasUnread
    ? `Notifications, ${String(unreadCount)} non lues`
    : 'Notifications, aucune non lue';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={label}
        aria-haspopup="dialog"
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Bell aria-hidden="true" className="h-5 w-5" />
        {hasUnread ? (
          <span
            aria-hidden="true"
            className="absolute -right-0.5 -top-0.5 inline-flex min-w-[1.125rem] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-4 text-primary-foreground"
          >
            {badgeText(unreadCount)}
          </span>
        ) : null}
      </button>
      <NotificationPanel
        open={open}
        onClose={() => setOpen(false)}
        onItemActivate={onItemActivate}
      />
    </>
  );
}
